import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useArchitectAgentEnhance } from "../../hooks/useArchitectAgentEnhance";
import type { API } from "../../types";
import { formatApiDisplayName } from "../../utils/apiDisplayName";
import {
  buildApiDescriptionEnhancePrompt,
  parseApiDescriptionEnhancement,
} from "../../utils/architectAgentOutput";

interface EnhanceApiDescriptionDialogProps {
  api: API | null;
  open: boolean;
  onClose: () => void;
  onAccept: (description: string) => void;
}

const EnhanceApiDescriptionDialog: React.FC<EnhanceApiDescriptionDialogProps> =
  ({ api, open, onClose, onAccept }) => {
    const { sendMessage, loading, error } = useArchitectAgentEnhance(
      api?.tenantId ?? null,
    );
    const [previewDescription, setPreviewDescription] = useState<string | null>(
      null,
    );
    const [parseError, setParseError] = useState<string | null>(null);

    const prompt = useMemo(
      () =>
        api
          ? buildApiDescriptionEnhancePrompt({
              name: api.name,
              version: api.version,
              uuid: api.uuid,
              description: api.description,
              specContent: api.specContent,
            })
          : "",
      [api],
    );

    useEffect(() => {
      if (!open) return;
      setPreviewDescription(null);
      setParseError(null);
    }, [open, api]);

    const handleClose = () => {
      setPreviewDescription(null);
      setParseError(null);
      onClose();
    };

    const handleSend = async () => {
      setParseError(null);
      try {
        const response = await sendMessage(prompt);
        const parsed = parseApiDescriptionEnhancement(response);
        if (!parsed) {
          setParseError(
            "Could not parse an enhanced description from the agent response.",
          );
          return;
        }
        setPreviewDescription(parsed);
      } catch {
        /* hook handles error */
      }
    };

    const handleAccept = () => {
      if (previewDescription != null) {
        onAccept(previewDescription);
      }
      handleClose();
    };

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Enhance API description</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {api ? formatApiDisplayName(api.name) : "API"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate an improved catalog description using the API spec and
            current description.
          </Typography>
          {(error || parseError) && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error ?? parseError}
            </Alert>
          )}

          {previewDescription != null && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={6}
                value={previewDescription}
                onChange={(e) => setPreviewDescription(e.target.value)}
                margin="normal"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          {previewDescription != null ? (
            <>
              <Button onClick={() => setPreviewDescription(null)}>Discard</Button>
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" color="success" onClick={handleAccept}>
                Accept
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button variant="contained" onClick={handleSend} disabled={loading}>
                {loading ? "Thinking..." : "Send to AI"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

export default EnhanceApiDescriptionDialog;
